/**
 * Theme toggle — botão sol/lua/auto que cicla light → dark → system → light.
 *
 * O atributo `[data-theme-icon="light|dark|system"]` em SVGs dentro do botão
 * controla qual ícone aparece para o tema ARMAZENADO (Theme, não ResolvedTheme).
 *
 * Suporta múltiplos botões (header desktop + mobile menu).
 */

import { cycleTheme, getTheme, onThemeChange, type Theme } from '../lib/theme';

function showIcon(button: Element, stored: Theme): void {
  const icons = button.querySelectorAll<HTMLElement>('[data-theme-icon]');
  icons.forEach((icon) => {
    const which = icon.getAttribute('data-theme-icon');
    icon.style.display = which === stored ? '' : 'none';
  });
}

function bindButton(button: HTMLElement): () => void {
  const apply = (): void => showIcon(button, getTheme());
  const onClick = (): void => {
    cycleTheme();
    apply();
  };

  apply();
  button.addEventListener('click', onClick);

  return () => {
    button.removeEventListener('click', onClick);
  };
}

/**
 * Inicializa todos os botões com ids `themeToggle` ou `themeToggleMobile`.
 * Também subscreve ao OS change (via onThemeChange) para refletir mudanças
 * que aconteceram fora do botão.
 */
export function initThemeToggle(): () => void {
  const buttons: HTMLElement[] = [];
  ['themeToggle', 'themeToggleMobile'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) buttons.push(el);
  });

  if (buttons.length === 0) return () => {};

  const cleanups: Array<() => void> = buttons.map(bindButton);

  // Quando o tema EFETIVO muda (incluindo via OS), atualiza ícones de todos
  // os botões — o tema armazenado pode ter sido 'system' o tempo todo.
  cleanups.push(
    onThemeChange(() => {
      const stored = getTheme();
      buttons.forEach((b) => showIcon(b, stored));
    }),
  );

  return () => {
    cleanups.forEach((fn) => fn());
  };
}
